import { UAParser } from 'ua-parser-js';
import { browserSupportConfig } from 'services/browserSupportConfig';

// Matches the current browser against the admin-editable "unsupported browser"
// rules (AdminPortalV2 > Browser Support, loaded by services/browserSupportConfig.js).
// This only ever drives a friendly warning — it never blocks play.
//
// Rule shape: { id, title?, message?, conditions: [{ field, operator, value }] }
// All conditions in a rule must match. Fields are ua-parser-js result paths.

const ACK_KEY = 'gc_browser_warning_ack';
const isProduction = process.env.REACT_APP_ENVIRONMENT === 'production';

// ua-parser-js strips the "AFT" prefix from Fire TV model codes (AFTSS -> "SS") and
// doesn't recognise the Roku browser at all. Fill in the same model codes the admin
// portal shows in room details (FireTvModels.cs / RokuModels.cs, same regexes as
// telemetry/capabilities.ts) so rules and room logs agree.
// Keep in sync with AdminPortalV2 ClientApp utils/browserSupport.js (normalize + matching).
export const normalizeDeviceDetails = (result) => {
    const ua = result?.ua || '';
    const device = { ...(result?.device || {}) };
    const fireTv = /\b(AFT[0-9A-Z]+)\b/i.exec(ua);
    const roku = /\((STB|TV)-([0-9A-Z]+)-([\d.]+)\)/i.exec(ua);

    if (fireTv) {
        device.vendor = device.vendor || 'Amazon';
        device.model = fireTv[1].toUpperCase();
        device.type = device.type || 'smarttv';
    } else if (roku) {
        device.vendor = 'Roku';
        device.model = roku[2].toUpperCase();
        device.type = 'smarttv';
    }

    return { ...result, device };
};

const getFieldValue = (uaResult, field) => {
    const value = field === 'ua'
        ? uaResult.ua
        : field.split('.').reduce((obj, key) => obj?.[key], uaResult);
    return value == null ? '' : String(value).toLowerCase();
};

const conditionMatches = (uaResult, { field, operator, value }) => {
    const actual = getFieldValue(uaResult, field);
    const expected = String(value ?? '').toLowerCase().trim();
    if (!expected) return false;

    switch (operator) {
        case 'equals': return actual === expected;
        case 'notEquals': return actual !== expected;
        case 'contains': return actual.includes(expected);
        case 'startsWith': return actual.startsWith(expected);
        case 'lessThan':
        case 'greaterThan': {
            const a = parseFloat(actual);
            const b = parseFloat(expected);
            if (Number.isNaN(a) || Number.isNaN(b)) return false;
            return operator === 'lessThan' ? a < b : a > b;
        }
        default: return false;
    }
};

export const findMatchingRule = (config, uaResult) => {
    if (!config?.enabled || !Array.isArray(config.rules)) return null;
    return config.rules.find(rule =>
        Array.isArray(rule.conditions) &&
        rule.conditions.length > 0 &&
        rule.conditions.every(condition => conditionMatches(uaResult, condition))
    ) || null;
};

// Small stable hash so an acknowledgement is invalidated when the wording changes
const hashText = (text) => {
    let hash = 5381;
    for (let i = 0; i < text.length; i++) hash = ((hash << 5) + hash + text.charCodeAt(i)) | 0;
    return (hash >>> 0).toString(36);
};

const ackKeyFor = (warning) => `${warning.ruleId}:${hashText(`${warning.title}|${warning.message}`)}`;

const isAcknowledged = (warning) => {
    try {
        return localStorage.getItem(ACK_KEY) === ackKeyFor(warning);
    } catch {
        return false;
    }
};

export const acknowledgeBrowserWarning = (warning) => {
    if (!warning || warning.preview) return;
    try {
        localStorage.setItem(ACK_KEY, ackKeyFor(warning));
    } catch { /* storage blocked — they'll just see it again next time */ }
};

/**
 * Resolves to the warning to show ({ ruleId, title, message }) or null.
 * Testing (non-production only):
 *   ?uaTest=<user agent>    — match rules against this UA instead of the real one
 *   ?browserWarning=force   — always show the warning (never remembered)
 */
export const getUnsupportedBrowserWarning = async () => {
    const params = new URLSearchParams(window.location.search);
    // Already normalized: built-in defaults until an admin saves, or if the API is down
    const config = await browserSupportConfig.load();

    if (!isProduction && params.get('browserWarning') === 'force') {
        return { ruleId: 'preview', title: config.title, message: config.message, preview: true };
    }

    const testUa = !isProduction ? params.get('uaTest') : null;
    const uaResult = normalizeDeviceDetails(new UAParser(testUa || undefined).getResult());

    const rule = findMatchingRule(config, uaResult);
    if (!rule) return null;

    const warning = {
        ruleId: rule.id,
        title: rule.title || config.title,
        message: rule.message || config.message,
        preview: !!testUa,
    };

    return !warning.preview && isAcknowledged(warning) ? null : warning;
};
